import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Alert,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CustomDialog from "../../components-new/common/CustomDialog";
import { useNotification } from "../../hooks";
import { parseExcelFile } from "../../components-new/utils/parseExcelFile";

const ROWS_PER_PAGE = 25;
const UPLOAD_CONCURRENCY = 5;

const UploadPrinciplesDialog = ({ open, onClose, onUploaded, addPrincipleMutation }) => {
  const notify = useNotification();

  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [nameCol, setNameCol] = useState("");
  const [principleCol, setPrincipleCol] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);

  const resetState = () => {
    setUploadFile(null);
    setUploadResult(null);
    setParsedRows([]);
    setColumns([]);
    setNameCol("");
    setPrincipleCol("");
    setSelectedRows([]);
    setPage(0);
  };

  const handleClose = () => {
    if (isUploading) return;
    resetState();
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    setUploadFile(file);
    setUploadResult(null);
    setSelectedRows([]);
    setParsedRows([]);
    setColumns([]);
    setNameCol("");
    setPrincipleCol("");
    setPage(0);
    if (!file) return;
    try {
      const { rows, headers } = await parseExcelFile(file);
      if (rows.length === 0 || headers.length === 0) {
        notify("No rows found in the uploaded file.", "error");
        return;
      }
      const visibleHeaders = headers.filter((h) => h.trim().toLowerCase() !== "description");
      setParsedRows(rows);
      setColumns(visibleHeaders);
    } catch {
      notify("Failed to read the Excel file. Please check the format.", "error");
    }
  };

  const pageIndices = parsedRows
    .map((_, i) => i)
    .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const selectedOnPage = pageIndices.filter((i) => selectedRows.includes(i)).length;

  const toggleRow = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleSelectAll = () => {
    const allPageSelected =
      pageIndices.length > 0 && pageIndices.every((i) => selectedRows.includes(i));
    setSelectedRows((prev) =>
      allPageSelected
        ? prev.filter((i) => !pageIndices.includes(i))
        : [...new Set([...prev, ...pageIndices])],
    );
  };

  const handleUpload = async () => {
    if (!nameCol || !principleCol) return;
    const rowsToUpload = selectedRows
      .map((i) => ({
        name: String(parsedRows[i]?.[nameCol] ?? "").trim(),
        principle: String(parsedRows[i]?.[principleCol] ?? "").trim(),
      }))
      .filter((r) => r.name && r.principle);
    if (rowsToUpload.length === 0) {
      notify("Selected rows have empty Name or Principle values.", "error");
      return;
    }
    setIsUploading(true);
    setUploadResult(null);
    let success = 0;
    const failed = [];
    // Upload in bounded batches so a large selection doesn't fire hundreds of
    // requests at once. allSettled keeps per-row reporting on partial failure.
    for (let start = 0; start < rowsToUpload.length; start += UPLOAD_CONCURRENCY) {
      const batch = rowsToUpload.slice(start, start + UPLOAD_CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map((row) =>
          addPrincipleMutation.mutateAsync({
            principleDescription: row.principle,
            principleName: row.name,
          }),
        ),
      );
      results.forEach((res, idx) => {
        if (res.status === "fulfilled") success += 1;
        else failed.push(batch[idx].name || `Row ${start + idx + 1}`);
      });
    }
    onUploaded?.();
    setUploadResult({ success, failed });
    setIsUploading(false);
    if (failed.length === 0) {
      notify(`${success} design principle(s) uploaded successfully`, "success");
      resetState();
      onClose();
    } else {
      notify(`${success} added, ${failed.length} failed`, "error");
    }
  };

  const content = (
    <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
      <Typography variant="body2" color="text.secondary">
        Map which columns hold the Name and Principle, then tick the rows to import.
      </Typography>

      <Button
        component="label"
        disabled={isUploading}
        sx={{
          border: "1.5px dashed #B9C6E8",
          borderRadius: 2,
          bgcolor: "#F7F9FE",
          color: "#2550D1",
          textTransform: "none",
          py: 1.75,
          gap: 1,
          "&:hover": { bgcolor: "#EEF2FC", borderColor: "#2550D1" },
        }}
      >
        <UploadFileIcon fontSize="small" />
        {uploadFile ? uploadFile.name : "Choose Excel file (.xlsx)"}
        <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileChange} />
      </Button>

      {columns.length > 0 && (
        <Box display="flex" gap={2}>
          <Autocomplete
            fullWidth
            size="small"
            options={columns}
            value={nameCol || null}
            onChange={(e, newValue) => setNameCol(newValue || "")}
            disabled={isUploading}
            renderInput={(params) => <TextField {...params} label="Name Column" />}
          />
          <Autocomplete
            fullWidth
            size="small"
            options={columns}
            value={principleCol || null}
            onChange={(e, newValue) => setPrincipleCol(newValue || "")}
            disabled={isUploading}
            renderInput={(params) => <TextField {...params} label="Principle Column" />}
          />
        </Box>
      )}

      {parsedRows.length > 0 && (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "0.5px solid #E2E6F3", borderRadius: 2, maxHeight: 320 }}
          >
            <Table size="small" stickyHeader>
              <TableHead sx={{ "& th": { backgroundColor: "#FFFFFF", zIndex: 4 } }}>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: "#F4F6FB", borderBottom: "1px solid #E2E6F3", zIndex: 3 }}>
                    <Checkbox
                      checked={pageIndices.length > 0 && selectedOnPage === pageIndices.length}
                      indeterminate={selectedOnPage > 0 && selectedOnPage < pageIndices.length}
                      onChange={toggleSelectAll}
                      disabled={isUploading}
                      sx={{ color: "#2550D1", "&.Mui-checked": { color: "#2550D1" } }}
                    />
                  </TableCell>
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ bgcolor: "#F4F6FB" }}>
                      <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
                        {col}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedRows
                  .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((row, sliceIndex) => {
                    const index = page * ROWS_PER_PAGE + sliceIndex;
                    const isSelected = selectedRows.includes(index);
                    return (
                      <TableRow
                        key={row.__rowId}
                        hover
                        selected={isSelected}
                        sx={{ "&.Mui-selected": { bgcolor: "#F7F9FE" } }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleRow(index)}
                            disabled={isUploading}
                            sx={{ color: "#2550D1", "&.Mui-checked": { color: "#2550D1" } }}
                          />
                        </TableCell>
                        {columns.map((col) => (
                          <TableCell key={col}>{String(row[col] ?? "")}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#E6ECFB",
                color: "#1e40a6",
                fontSize: 12,
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: 5,
              }}
            >
              {selectedRows.length} of {parsedRows.length} selected
            </Box>
            <TablePagination
              component="div"
              count={parsedRows.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              sx={{ border: "none" }}
            />
          </Box>
        </>
      )}

      {uploadResult && uploadResult.failed.length > 0 && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {uploadResult.success} added. Failed: {uploadResult.failed.join(", ")}
        </Alert>
      )}
    </Box>
  );

  const actions = (
    <>
      <Button onClick={handleClose} color="inherit" disabled={isUploading}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={isUploading || !nameCol || !principleCol || selectedRows.length === 0}
        sx={{ bgcolor: "#2550D1", "&:hover": { bgcolor: "#1e40a6" } }}
      >
        {isUploading ? "Uploading..." : `Upload (${selectedRows.length})`}
      </Button>
    </>
  );

  return (
    <CustomDialog
      open={open}
      width={900}
      onClose={handleClose}
      title="Upload Design Principles"
      content={content}
      actions={actions}
    />
  );
};

export default UploadPrinciplesDialog;